import { useCallback } from "react";
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Typography,
  useTheme,
} from "@mui/material";
import { IconChartBar } from "@tabler/icons-react";
import PagamentoDb from "../../../db/pagamentoDb";
import { getDocs, query, where } from "firebase/firestore";
import { filter, groupBy, map, orderBy, sumBy, uniqBy } from "lodash";
import { useUsuario } from "../../../providers/usuarioProvider";
import { TipoPagamento } from "../../../models/pagamento";
import MoedaText from "../../util/ui/inputs/moedaText";
import FelixCard from "../../util/ui/felixCard";
import ChartContainer from "../../util/charts/chartContainer";
import usePromise from "../../util/usePromise";
import moment from "moment";
import Area from "../../util/charts/area";
import { Serie, SliceTooltipProps } from "@nivo/line";
import chroma from "chroma-js";
import Chart from "react-google-charts";
import { felixTokens, radius } from "../../../theme";

export default function CardProventos() {
  const theme = useTheme();
  const { visualizado } = useUsuario();
  const obterProventosPromise = useCallback(() => {
    if (visualizado)
      return getDocs(
        query(
          new PagamentoDb().collection({
            usuarioId: visualizado,
            pagamentoId: "",
          }),
          where(
            "dataCredito",
            ">=",
            moment().subtract(1, "year").startOf("year").toDate()
          )
        )
      ).then((query) => map(query.docs, (doc) => doc.data()));
    else return Promise.resolve([]);
  }, [visualizado]);
  const { data: proventos = [], loading } = usePromise(obterProventosPromise);

  const proventosPorMeses = groupBy(orderBy(proventos, "dataCredito"), (p) =>
    p.dataCredito.format("YYYY-MM")
  );

  const keys = orderBy(
    map(uniqBy(proventos, "codigoAtivo"), (provento) => {
      return provento.codigoAtivo;
    }),
    (c) => c,
    "desc"
  );

  const data: Serie[] = map(keys, (codigoAtivo, index) => {
    return {
      id: codigoAtivo,
      data: map(proventosPorMeses, (proventos, mes) => {
        return {
          x: moment(mes, "YYYY-MM").endOf("month").format("YYYY-MM-DD"),
          y: sumBy(
            filter(
              proventos,
              (provento) => provento.codigoAtivo == codigoAtivo
            ),
            (p) => {
              return p.tipo === TipoPagamento.Juros
                ? p.pagamento * 0.85
                : p.pagamento;
            }
          ),
        };
      }),
      color: chroma(theme.palette.success.main)
        .luminance(0.1 + 0.8 * (index / keys.length))
        .hex(),
    };
  });

  const chartData = [
    ["Mês", ...keys],
    ...map(proventosPorMeses, (proventos, mes) => {
      const row = [
        moment(mes, "YYYY-MM").endOf("month").format("MMM YYYY"),
        ...map(keys, (codigoAtivo) => {
          return sumBy(
            filter(
              proventos,
              (provento) => provento.codigoAtivo === codigoAtivo
            ),
            (p) =>
              p.tipo === TipoPagamento.Juros ? p.pagamento * 0.85 : p.pagamento
          );
        }),
      ];
      return row;
    }),
  ];

  const options = {
    hAxis: {
      title: "Mês",
      textStyle: {
        color: chroma(theme.palette.text.primary).alpha(1).hex(), // Cor do texto no eixo horizontal
      },
      titleTextStyle: {
        color: chroma(theme.palette.text.secondary).alpha(1).hex(), // Cor do título no eixo horizontal
      },
    },
    vAxis: {
      title: "Valor (R$)",
      minValue: 0,
      textStyle: {
        color: chroma(theme.palette.text.primary).alpha(1).hex(), // Cor do texto no eixo horizontal
      },
      titleTextStyle: {
        color: chroma(theme.palette.text.secondary).alpha(1).hex(), // Cor do título no eixo horizontal
      },
    },
    isStacked: true,
    colors: keys.map((_, index) =>
      chroma(theme.palette.success.main)
        .luminance(0.1 + 0.8 * (index / keys.length))
        .hex()
    ),
    backgroundColor: "transparent",
    fontName: "Mabry Pro",
    legend: {
      textStyle: {
        color: chroma(theme.palette.text.secondary).alpha(1).hex(), // Cor personalizada para a legenda
      },
    },
  };

  const tooltip = ({ slice }: SliceTooltipProps) => {
    const proventosPorMes = slice.points;
    return (
      // Tooltip de gráfico: surface + hairline vindos do Card do tema.
      <Card
        elevation={0}
        sx={{ boxShadow: felixTokens.shadow.overlay, borderRadius: radius.sm }}
      >
        <Box display="flex" flexDirection="column" padding={1.5} minWidth={180}>
          <Typography variant="labelMono" color="text.secondary">
            {moment(slice.points[0].data.x).format("MMMM [de] YYYY")}
          </Typography>
          <Box paddingY={0.5} />
          {map(proventosPorMes, ({ serieId: codigoAtivo, data }) => {
            const value = data.y as number;
            return value > 0 ? (
              <Box
                key={String(codigoAtivo)}
                display="flex"
                flexDirection="row"
                justifyContent="space-between"
                gap={2}
              >
                <Typography variant="labelMono" color="text.secondary">
                  {codigoAtivo}
                </Typography>
                <Typography variant="dataMono" color="text.primary">
                  {<MoedaText value={value} />}
                </Typography>
              </Box>
            ) : (
              <></>
            );
          })}
          <Divider sx={{ my: 1 }} />
          <Box
            display="flex"
            flexDirection="row"
            justifyContent="space-between"
            gap={2}
          >
            <Typography variant="labelMono" color="text.secondary">
              Total
            </Typography>
            <Typography
              variant="dataMono"
              color="text.primary"
              sx={{ fontWeight: 600 }}
            >
              {<MoedaText value={sumBy(proventosPorMes, "data.y")} />}
            </Typography>
          </Box>
        </Box>
      </Card>
    );
  };

  return (
    <FelixCard>
      <CardHeader title="Proventos" avatar={<IconChartBar size={16} />} />
      <CardContent>
        <ChartContainer
          height={300}
          emptyText="Ainda não há resultados mensais."
          isEmpty={data.length === 0}
          isLoading={loading}
        >
          {/* <Chart
            chartType="ColumnChart"
            data={chartData}
            options={options}
            width="100%"
            height="300px"
            loader={<div>Carregando gráfico...</div>}
          /> */}
          <Area
            data={data}
            tooltip={tooltip}
            // colorTheme="positives"
          />
        </ChartContainer>
      </CardContent>
    </FelixCard>
  );
}
